<script>
import { debounce, cloneDeep, isEqual } from 'lodash';
import ActivityFilter from '../filters/activity_filter.vue';
import ProjectFilter from '../filters/project_filter.vue';
import ClusterFilter from '../filters/cluster_filter.vue';
import ImageFilter from '../filters/image_filter.vue';
import ToolFilter from '../filters/tool_filter.vue';
import ToolWithVendorFilter from '../filters/tool_with_vendor_filter.vue';
import SeverityFilter from '../filters/severity_filter.vue';
import StatusFilter from '../filters/status_filter.vue';
import PipelineStatusFilter from '../filters/pipeline_status_filter.vue';
import { FILTERS } from './constants';

// Due to legacy reasons, some scanner vendors are blank. We'll treat these as GitLab scanners.
const DEFAULT_VENDORS = ['', 'GitLab'];

export default {
  inject: {
    scanners: {
      type: Array,
      required: false,
      default: [],
    },
  },
  props: {
    filters: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      filterQuery: {},
      // When this component is first shown, every filter will emit its own @filter-changed event at
      // the same time, which will trigger this method multiple times. We'll debounce it so that it
      // only runs once. Note that this is in data() so that it's unique per instance. Otherwise,
      // every instance of this component will share the same debounce function.
      emitFilterChange: debounce(function emit() {
        this.$emit('filters-changed', this.filterQuery);
      }),
    };
  },
  methods: {
    getComponentType(filter) {
      switch (filter) {
        case FILTERS.SEVERITY:
          return SeverityFilter;
        case FILTERS.STATUS:
          return StatusFilter;
        case FILTERS.PIPELINE_STATUS:
          return PipelineStatusFilter;
        case FILTERS.TOOL_VENDOR: {
          const hasCustomVendor = (vendor) => !DEFAULT_VENDORS.includes(vendor);
          return this.scanners.some(({ vendor }) => hasCustomVendor(vendor))
            ? ToolWithVendorFilter // if there is at least 1 scanner with a custom vendor
            : ToolFilter;
        }
        case FILTERS.TOOL_SIMPLE:
          return ToolFilter;
        case FILTERS.TOOL_PIPELINE:
          return ToolFilter;
        case FILTERS.ACTIVITY:
          return ActivityFilter;
        case FILTERS.PROJECT:
          return ProjectFilter;
        case FILTERS.CLUSTER:
          return ClusterFilter;
        case FILTERS.IMAGE:
          return ImageFilter;
        default:
          throw new Error(`Invalid filter type: ${filter}`);
      }
    },
    updateFilterQuery(query) {
      const oldQuery = cloneDeep(this.filterQuery);
      this.filterQuery = { ...this.filterQuery, ...query };
      // Don't emit if the filters didn't change because it will trigger the GraphQL queries to run.
      if (!isEqual(oldQuery, this.filterQuery)) {
        this.emitFilterChange();
      }
    },
  },
};
</script>

<template>
  <div
    class="vulnerability-report-filters gl-border-b-1 gl-border-b-default gl-bg-subtle gl-p-5 gl-border-b-solid"
  >
    <component
      :is="getComponentType(filter)"
      v-for="filter in filters"
      :key="filter"
      @filter-changed="updateFilterQuery"
    />
  </div>
</template>
